import React, { useState } from 'react';
import { Home, Users as UsersIcon, Briefcase, BarChart3, Bookmark, Plus, ChevronRight, Store } from '../../components/CustomIcons';
import { Btn, Chip, Mono, ScreenHeader } from '../Prototype';
import { StatCard, SectionTitle, StaffShell, Badge, useUI } from './shared';
import { EP_IDENTITY, EP_JOB_POSTINGS, EP_IMPACT, LEARNERS } from './data';

const NAV = [
  { key: 'dashboard', label: 'Dashboard', icon: Home },
  { key: 'candidates', label: 'Candidates', icon: UsersIcon },
  { key: 'positions', label: 'Positions', icon: Briefcase },
  { key: 'impact', label: 'Impact', icon: BarChart3 },
  { key: 'marketplace', label: 'Marketplace', icon: Store },
];

const useEPState = () => {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem('assistivos_employer_state');
      if (raw) return JSON.parse(raw);
    } catch (_e) { /* ignore */ }
    return { tab: 'dashboard', selectedCandidateId: null, interests: {}, savedCandidates: [] };
  });
  const update = (patch) => setState((s) => {
    const next = { ...s, ...(typeof patch === 'function' ? patch(s) : patch) };
    try { localStorage.setItem('assistivos_employer_state', JSON.stringify(next)); } catch (_e) { /* ignore */ }
    return next;
  });
  return [state, update];
};

const EP1_Home = ({ update }) => (
  <>
    <ScreenHeader title={EP_IDENTITY.company} subtitle="Partner Portal" />
    <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
      <div className="rounded-[12px] p-4 text-center" style={{ background: 'rgba(249,115,22,0.10)', border: '1px solid #f97316' }}>
        <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#c2410c' }}>Verified Second-Chance Employer</div>
        <div className="text-[13px] text-[#0f2744] mt-1">TTI Partner Since {EP_IDENTITY.partnerSince}</div>
      </div>
      <section aria-label="Impact stats" className="grid grid-cols-3 gap-2">
        <StatCard label="Matched" value={EP_IMPACT.matched} testId="ep-stat-matched" />
        <StatCard label="Hires" value={EP_IMPACT.hires} accent testId="ep-stat-hires" />
        <StatCard label="90-Day Retention" value={`${EP_IMPACT.retention}%`} testId="ep-stat-retention" />
      </section>
      <section aria-label="Job postings">
        <SectionTitle right={`${EP_JOB_POSTINGS.length} active`}>Active Positions</SectionTitle>
        <ul className="list-none p-0 space-y-2">
          {EP_JOB_POSTINGS.map((j) => (
            <li key={j.id} className="rounded-[12px] bg-[#f1f5f9] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="p-display text-[17px]" style={{ color: '#0f2744' }}>{j.title}</div>
                  <div className="text-[12px] text-[#64748b] mt-0.5">{j.department}</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Chip mono>{j.salary}</Chip>
                    <Chip><Mono>{j.matched}</Mono> matched</Chip>
                    {j.secondChance && <Badge tone="success">Second-Chance</Badge>}
                  </div>
                </div>
              </div>
              <Btn variant="ghost" className="mt-3 w-full" onClick={() => update({ tab: 'candidates' })} data-testid={`ep-view-candidates-${j.id}`}>View Candidates</Btn>
            </li>
          ))}
        </ul>
      </section>
      <section className="grid grid-cols-2 gap-2">
        <Btn variant="ghost" onClick={() => update({ tab: 'positions' })} data-testid="ep-post-btn">Post Position</Btn>
        <Btn variant="ghost" onClick={() => update({ tab: 'impact' })} data-testid="ep-report-btn">Impact Report</Btn>
      </section>
    </main>
  </>
);

const EP2_Candidates = ({ state, update }) => {
  const { showModal, showToast } = useUI();
  const candidates = LEARNERS.filter((l) => l.moduleId === 'hvac');
  const expressInterest = (c) => showModal({
    title: `Express interest in ${c.firstName} ${c.lastInitial}?`,
    body: 'TTI will coordinate next steps with the facility case manager.',
    confirmLabel: 'Confirm Interest',
    onConfirm: () => {
      update((s) => ({ interests: { ...s.interests, [c.id]: true } }));
      showToast(`Interest expressed. TTI liaison notified.`, 'success');
    },
  });
  return (
    <>
      <ScreenHeader title="Matched Candidates" subtitle="HVAC Technician" />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-3">
        <p className="text-[13px] text-[#64748b]">Based on credential pathway alignment. Full names released upon facility approval.</p>
        <ul className="list-none p-0 space-y-3">
          {candidates.map((c) => {
            const interested = state.interests[c.id];
            return (
              <li key={c.id} className="rounded-[12px] p-4" style={{ background: '#f1f5f9', border: `1px solid ${interested ? '#f97316' : 'transparent'}` }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="p-display text-[17px]" style={{ color: '#0f2744' }}>{c.firstName} {c.lastInitial}</div>
                    <div className="text-[12px] text-[#64748b] mt-0.5">Release <Mono>{c.releaseDate}</Mono></div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {c.credentialsEarned.map((cr) => <Chip key={cr.name}>{cr.name} ✓</Chip>)}
                      {c.credentialsInProgress.map((cr) => <Chip key={cr.name}>{cr.name} <Mono>{cr.pct}</Mono>%</Chip>)}
                    </div>
                  </div>
                  <Badge tone={c.matchStrength === 'strong' ? 'success' : 'navy'}>
                    {c.matchStrength === 'strong' ? 'Strong Match' : 'Good Match'}
                  </Badge>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Btn variant="ghost" onClick={() => update({ selectedCandidateId: c.id, view: 'detail' })} data-testid={`ep-candidate-${c.id}`}>Learn More</Btn>
                  {interested ? (
                    <button
                      disabled
                      className="min-h-[48px] rounded-[12px] font-semibold text-[15px] bg-[#f97316] text-[#0f2744] opacity-90"
                      data-testid={`ep-interested-${c.id}`}
                    >
                      Interest Expressed ✓
                    </button>
                  ) : (
                    <Btn onClick={() => expressInterest(c)} data-testid={`ep-interest-${c.id}`}>Express Interest</Btn>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </main>
    </>
  );
};

const EP3_CandidateDetail = ({ state, update }) => {
  const c = LEARNERS.find((x) => x.id === state.selectedCandidateId);
  const { showModal, showToast } = useUI();
  if (!c) return null;
  const interested = state.interests[c.id];
  return (
    <>
      <ScreenHeader
        title={`${c.firstName} ${c.lastInitial}`}
        subtitle="Candidate Profile"
        onBack={() => update({ view: null })}
        breadcrumbs={[
          { label: 'Dashboard', onClick: () => update({ tab: 'dashboard', view: null }) },
          { label: 'Candidates', onClick: () => update({ tab: 'candidates', view: null }) },
          { label: `${c.firstName} ${c.lastInitial}` },
        ]}
      />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
        <div className="rounded-[12px] p-3 text-[12px]" style={{ background: 'rgba(15,39,68,0.06)', color: '#0f2744' }}>
          Full name and contact released upon facility approval at release.
        </div>
        <section aria-label="Credentials">
          <SectionTitle>Credentials</SectionTitle>
          <div className="flex flex-wrap gap-2">
            {c.credentialsEarned.map((cr) => <Chip key={cr.name}>✓ {cr.name}</Chip>)}
            {c.credentialsInProgress.map((cr) => <Chip key={cr.name}>{cr.name} <Mono>{cr.pct}</Mono>%</Chip>)}
          </div>
        </section>
        <section aria-label="Skills">
          <SectionTitle>Skill Areas</SectionTitle>
          <p className="text-[14px] text-[#1c1c1e] leading-relaxed">{c.skills}</p>
        </section>
        <section aria-label="Details" className="grid grid-cols-2 gap-2">
          <div className="rounded-[12px] bg-[#f1f5f9] p-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748b]">Anticipated release</div>
            <div className="p-mono text-[15px] mt-1 text-[#0f2744]">{c.releaseDate}</div>
          </div>
          <div className="rounded-[12px] bg-[#f1f5f9] p-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748b]">Education</div>
            <div className="text-[14px] mt-1 text-[#0f2744]">{c.education}</div>
          </div>
        </section>
        <section aria-label="Case manager note" className="rounded-[12px] bg-[#f1f5f9] p-4">
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#64748b]">Case manager note</div>
          <p className="text-[14px] mt-1 text-[#0f2744] leading-relaxed italic">&ldquo;{c.caseNote}&rdquo;</p>
        </section>
        <section aria-label="Match" className="rounded-[12px] p-4" style={{ background: 'rgba(21,128,61,0.08)', border: '1px solid #15803d' }}>
          <div className="text-[13px] font-bold text-[#15803d]">Matched: HVAC Technician — Entry Level</div>
          <div className="text-[13px] mt-1 text-[#166534]">Salary <Mono>$45,000–$55,000</Mono> — aligned with your posting.</div>
        </section>
      </main>
      <footer className="px-6 py-4 border-t border-[#e2e8f0] bg-[#fafaf9] grid grid-cols-2 gap-2">
        <Btn variant="ghost" onClick={() => showToast('Profile saved.')} data-testid="ep-save-profile"><Bookmark className="w-4 h-4" /> Save Profile</Btn>
        {interested ? (
          <button disabled className="min-h-[48px] rounded-[12px] font-semibold text-[15px] bg-[#f97316] text-[#0f2744]">Interest Expressed ✓</button>
        ) : (
          <Btn onClick={() => showModal({
            title: `Express interest in ${c.firstName} ${c.lastInitial}?`,
            body: 'TTI will coordinate next steps with the facility case manager.',
            confirmLabel: 'Confirm Interest',
            onConfirm: () => {
              update((s) => ({ interests: { ...s.interests, [c.id]: true } }));
              showToast(`Interest expressed. TTI liaison notified.`, 'success');
            },
          })} data-testid="ep-express">Express Interest</Btn>
        )}
      </footer>
    </>
  );
};

const EP4_PostPosition = ({ update }) => {
  const { showToast } = useUI();
  const [posted, setPosted] = useState(false);
  return (
    <>
      <ScreenHeader title="Post a Position" subtitle="TechForce Solutions" />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
        {[
          { l: 'Job title', v: 'HVAC Technician — Entry Level' },
          { l: 'Department', v: 'Building Systems' },
          { l: 'Location', v: 'Dallas, TX (multiple sites)' },
          { l: 'Salary range', v: '$45,000 – $55,000', mono: true },
        ].map((f) => (
          <div key={f.l}>
            <label className="text-[12px] font-bold uppercase tracking-widest text-[#64748b]">{f.l}</label>
            <input defaultValue={f.v} className="mt-1 w-full min-h-[48px] rounded-[12px] border border-[#e2e8f0] bg-[#f1f5f9] px-4 text-[14px] focus:outline-none focus:border-[#f97316]" style={f.mono ? { fontFamily: 'var(--p-font-mono)' } : undefined} />
          </div>
        ))}
        <div>
          <label className="text-[12px] font-bold uppercase tracking-widest text-[#64748b]">Required credentials</label>
          <div className="mt-1 flex flex-wrap gap-2">
            <Chip>CompTIA A+</Chip>
            <Chip>EPA 608</Chip>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-[12px] bg-[#f1f5f9] p-3">
          <span className="text-[14px] font-semibold text-[#0f2744]">Open to second-chance candidates</span>
          <span className="rounded-full px-3 py-1 text-[12px] font-bold" style={{ background: '#15803d', color: '#fafaf9' }}>ON</span>
        </div>
        <div>
          <label className="text-[12px] font-bold uppercase tracking-widest text-[#64748b]">Description</label>
          <textarea rows={3} defaultValue="Install and service residential HVAC systems. Recover and recharge refrigerant per EPA 608 standards. Communicate service findings to customers." className="mt-1 w-full rounded-[12px] border border-[#e2e8f0] bg-[#f1f5f9] px-4 py-3 text-[14px] focus:outline-none focus:border-[#f97316]" />
        </div>
      </main>
      <footer className="px-6 py-4 border-t border-[#e2e8f0] bg-[#fafaf9]">
        <Btn className="w-full" disabled={posted} onClick={() => {
          setPosted(true);
          showToast('Position posted. TTI will match qualified candidates within 48 hours.', 'success');
        }} data-testid="ep-submit-post">
          {posted ? 'Position Posted ✓' : 'Post Position'}
        </Btn>
      </footer>
    </>
  );
};

const EP5_Impact = ({ update }) => {
  const { showToast } = useUI();
  const maxVal = Math.max(...EP_IMPACT.timeline.map((m) => m.matched));
  return (
    <>
      <ScreenHeader title="Your Impact" subtitle="Second-Chance Program" />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
        <section aria-label="Key metrics" className="grid grid-cols-2 gap-2">
          <StatCard label="Hires" value={EP_IMPACT.hires} accent />
          <StatCard label="Avg starting wage" value={EP_IMPACT.avgWage} />
          <StatCard label="90-Day Retention" value={`${EP_IMPACT.retention}%`} />
          <StatCard label="Pipeline" value={EP_IMPACT.pipeline} />
        </section>
        <section aria-label="Timeline">
          <SectionTitle>Match → Hire Timeline (2025)</SectionTitle>
          <div className="rounded-[12px] bg-[#f1f5f9] p-4">
            <div className="flex items-end justify-between gap-2" style={{ height: 140 }}>
              {EP_IMPACT.timeline.map((m) => {
                const mh = (m.matched / maxVal) * 100;
                const hh = (m.hired / maxVal) * 100;
                return (
                  <div key={m.month} className="flex flex-col items-center gap-1 flex-1">
                    <div className="flex items-end gap-0.5 h-full">
                      <div className="w-3 rounded-t" style={{ height: `${mh}%`, background: '#f97316' }} title={`${m.matched} matched`} />
                      <div className="w-3 rounded-t" style={{ height: `${hh}%`, background: '#0f2744' }} title={`${m.hired} hired`} />
                    </div>
                    <div className="text-[10px] text-[#64748b] font-mono">{m.month}</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex items-center gap-4 text-[12px] text-[#64748b]">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: '#f97316' }} /> Matched</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: '#0f2744' }} /> Hired</span>
            </div>
          </div>
        </section>
        <Btn onClick={() => showToast('Report exported as PDF.', 'success')} className="w-full" data-testid="ep-download-report">Download Full Report</Btn>
      </main>
    </>
  );
};

// ── EP-6: Marketplace Browse ───────────────────────────────────────────────────
const EP6_DEMO = [
  {
    id: 'mp-1', name: 'Sandra R.', module: 'Medical Assistant Essentials',
    pct: 74, creds: ['NHA CCMA', 'OSHA-10'], availability: 'Available: Q1 2026',
    readiness: 'Reentry-ready · 2 credentials earned',
  },
  {
    id: 'mp-2', name: 'Anthony B.', module: 'HVAC Fundamentals',
    pct: 90, creds: ['EPA 608', 'NCCER Core'], availability: 'Available now',
    readiness: 'Reentry-ready · 2 credentials earned',
  },
  {
    id: 'mp-3', name: 'Kevin L.', module: 'AI Foundations',
    pct: 100, creds: ['CompTIA AI+', 'NCCER Core'], availability: 'Available now',
    readiness: 'Reentry-ready · 3 credentials earned',
  },
];

const EP6_MarketplaceBrowse = ({ update }) => {
  const { showToast, showModal } = useUI();
  const [requested, setRequested] = useState({});

  const handleRequest = (c) => showModal({
    title: `Request introduction to ${c.name}?`,
    body: 'Your request goes to the facility case manager who will coordinate next steps. You will not contact the candidate directly.',
    confirmLabel: 'Send Request',
    onConfirm: () => {
      setRequested((prev) => ({ ...prev, [c.id]: true }));
      showToast(`Introduction requested. Case manager notified.`, 'success');
    },
  });

  return (
    <>
      <ScreenHeader title="Talent Marketplace" subtitle="Credential-verified participants" />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
        <p className="text-[13px] text-[#64748b]">
          Participants shown as first name + last initial. Case managers broker all introductions.
        </p>
        <ul className="list-none p-0 space-y-3">
          {EP6_DEMO.map((c) => (
            <li key={c.id} className="rounded-[12px] bg-white border border-[#e2e8f0] p-4" style={{ boxShadow: '0 1px 3px rgba(15,39,68,0.06)' }} data-testid={`ep6-card-${c.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1">
                  <div className="p-display text-[17px]" style={{ color: '#0f2744' }}>{c.name}</div>
                  <div className="text-[12px] text-[#64748b] mt-0.5">{c.module} · {c.pct}% complete</div>
                </div>
                <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: '#dcfce7', color: '#15803d' }}>
                  Reentry-ready
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {c.creds.map((cr) => (
                  <span key={cr} className="inline-flex items-center gap-1 rounded-[6px] px-2 py-0.5 text-[11px] font-bold" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', color: '#15803d' }}>
                    ✓ {cr}
                    <span className="text-[8px] font-black tracking-wide ml-1" style={{ background: 'rgba(34,197,94,0.15)', borderRadius: '3px', padding: '1px 4px' }}>VERIFIED</span>
                  </span>
                ))}
              </div>
              <div className="flex items-center justify-between text-[12px] mb-3">
                <span className="text-[#64748b]">{c.availability}</span>
                <span className="font-bold" style={{ color: '#f97316' }}>{c.readiness}</span>
              </div>
              {requested[c.id] ? (
                <button disabled className="w-full min-h-[44px] rounded-[12px] font-semibold text-[14px]" style={{ background: '#dcfce7', color: '#15803d', border: 'none' }} data-testid={`ep6-requested-${c.id}`}>
                  Introduction Requested ✓
                </button>
              ) : (
                <Btn className="w-full" onClick={() => handleRequest(c)} data-testid={`ep6-request-${c.id}`}>
                  Request Introduction
                </Btn>
              )}
            </li>
          ))}
        </ul>
        <div className="mt-2">
          <Btn variant="ghost" className="w-full" onClick={() => update({ tab: 'onboarding' })} data-testid="ep6-join-cta">
            Complete Partner Onboarding →
          </Btn>
        </div>
      </main>
    </>
  );
};

// ── EP-7: Employer Onboarding Form ────────────────────────────────────────────
const EP7_Onboarding = ({ update }) => {
  const { showToast } = useUI();
  const [submitted, setSubmitted] = useState(false);

  return (
    <>
      <ScreenHeader title="Partner Onboarding" subtitle="TechForce Solutions" onBack={() => update({ tab: 'marketplace', view: null })} />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
        {[
          { l: 'Organization name', v: 'TechForce Solutions, Inc.' },
          { l: 'Contact name', v: 'David Ruiz' },
          { l: 'Contact email', v: 'druiz@techforce.com' },
        ].map((f) => (
          <div key={f.l}>
            <label className="text-[12px] font-bold uppercase tracking-widest text-[#64748b]">{f.l}</label>
            <input defaultValue={f.v} className="mt-1 w-full min-h-[44px] rounded-[12px] border border-[#e2e8f0] bg-[#f1f5f9] px-4 text-[14px] focus:outline-none focus:border-[#f97316]" />
          </div>
        ))}
        <div>
          <label className="text-[12px] font-bold uppercase tracking-widest text-[#64748b]">Second-chance commitment</label>
          <div className="mt-2 flex flex-col gap-2">
            {['Federal Bonding participant', 'WOTC-eligible employer', 'Fair Chance pledge signatory'].map((opt, i) => (
              <div key={opt} className="flex items-center gap-3 rounded-[10px] p-3" style={{ background: '#f1f5f9', border: `1.5px solid ${i === 0 ? '#0f2744' : 'transparent'}` }}>
                <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center" style={{ borderColor: i === 0 ? '#0f2744' : '#cbd5e1' }}>
                  {i === 0 && <div className="w-2 h-2 rounded-full" style={{ background: '#0f2744' }} />}
                </div>
                <span className="text-[13px] font-semibold text-[#0f2744]">{opt}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[12px] font-bold uppercase tracking-widest text-[#64748b]">Preferred credential types</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {['Trades', 'Technology'].map((c) => (
              <span key={c} className="rounded-full px-4 py-1.5 text-[13px] font-bold" style={{ background: '#0f2744', color: '#fff' }}>{c}</span>
            ))}
            {['Healthcare', 'Any'].map((c) => (
              <span key={c} className="rounded-full px-4 py-1.5 text-[13px] font-bold" style={{ background: '#f1f5f9', color: '#475569', border: '1.5px solid #e2e8f0' }}>{c}</span>
            ))}
          </div>
        </div>
      </main>
      <footer className="px-6 py-4 border-t border-[#e2e8f0] bg-[#fafaf9]">
        <Btn className="w-full" disabled={submitted} onClick={() => {
          setSubmitted(true);
          showToast('Partner onboarding complete! You now have full marketplace access.', 'success');
        }} data-testid="ep7-submit">
          {submitted ? 'Onboarding Complete ✓' : 'Complete Onboarding'}
        </Btn>
      </footer>
    </>
  );
};

const Employer = () => {
  const [state, update] = useEPState();
  const tab = state.tab || 'dashboard';
  const view = state.view;
  const activeKey = view === 'detail' ? 'candidates' : tab;
  const onNav = (k) => update({ tab: k, view: null });

  let screen;
  if (view === 'detail') screen = <EP3_CandidateDetail state={state} update={update} />;
  else if (tab === 'candidates') screen = <EP2_Candidates state={state} update={update} />;
  else if (tab === 'positions') screen = <EP4_PostPosition update={update} />;
  else if (tab === 'impact') screen = <EP5_Impact update={update} />;
  else if (tab === 'marketplace') screen = <EP6_MarketplaceBrowse update={update} />;
  else if (tab === 'onboarding') screen = <EP7_Onboarding update={update} />;
  else screen = <EP1_Home update={update} />;

  return (
    <StaffShell items={NAV} activeKey={activeKey} onChange={onNav} badge={{ initial: 'W', name: EP_IDENTITY.company, role: 'Employer Partner' }}>
      {screen}
    </StaffShell>
  );
};

export default Employer;
